import React, { useMemo, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Badge, useTheme } from "react-native-elements";
import { GameIcon } from "../utility/icons/GameIcon";
import { TooltipWrapper } from "../utility/Tooltip";

const DOUBLE_TAP_THRESHOLD_MS = 500;

export function ItemTile(props: {
  onPress?: () => void;
  onDoublePress?: () => void;
  title: string;
  icon?: string;
  amount?: number;
  disabled?: boolean;
  tooltipIdSuffix?: string;
  tooltip?: React.ReactNode;
}) {
  const theme = useTheme();
  const [lastTapTime, setLastTapTime] = useState(0);
  const inner = useMemo(
    () => (
      <View style={[styles.tile, { borderColor: theme.theme.colors.grey4 }]}>
        {props.icon && (
          <GameIcon
            style={props.disabled && { opacity: 0.2 }}
            icon={props.icon}
          />
        )}
        {props.amount != null && props.amount > 1 && (
          <Badge
            status="primary"
            value={props.amount}
            containerStyle={{ position: "absolute", top: -8, right: -8 }}
            badgeStyle={{ borderColor: theme.theme.colors.background }}
          />
        )}
      </View>
    ),
    [theme, props.icon, props.amount, props.disabled],
  );
  return useMemo(() => {
    if (!props.onPress) {
      return (
        <TooltipWrapper
          containerStyle={[
            styles.tile,
            { flex: null, borderColor: theme.theme.colors.grey4 },
          ]}
          tooltip={props.tooltip}
          tooltipId={props.title + props.tooltipIdSuffix}
        >
          {inner}
        </TooltipWrapper>
      );
    }
    return (
      <TooltipWrapper
        containerStyle={[
          styles.tile,
          { flex: null, borderColor: theme.theme.colors.grey4 },
        ]}
        tooltip={props.tooltip}
        tooltipId={props.title + props.tooltipIdSuffix}
      >
        <TouchableOpacity
          accessibilityLabel={
            props.title +
            (props.amount && props.amount > 1 ? ` (${props.amount})` : "")
          }
          accessibilityRole="button"
          onPress={() => {
            const currentTapTime = Date.now();
            if (currentTapTime - lastTapTime < DOUBLE_TAP_THRESHOLD_MS) {
              setLastTapTime(0);
              props.onDoublePress && props.onDoublePress();
            } else {
              setLastTapTime(currentTapTime);
              props.onPress && props.onPress();
            }
          }}
        >
          {inner}
        </TouchableOpacity>
      </TooltipWrapper>
    );
  }, [
    props.title,
    props.tooltip,
    props.onPress,
    props.onDoublePress,
    lastTapTime,
    setLastTapTime,
    inner,
    props.tooltipIdSuffix,
  ]);
}

const styles = StyleSheet.create({
  tile: {
    borderWidth: 0.5,
    height: 48,
    width: 48,
    justifyContent: "center",
    alignItems: "center",
    margin: 8,
  },
});
